import { parseFailureSummary, type FailureSummary } from './failureSummary.js';
import { partyOf, type Party } from './requirements.js';

/**
 * Monthly district summaries (owner ruling 2026-10-22, after round 3: "build
 * the monthly summaries before go-live"). One DistrictSummaries row per
 * district × Eastern calendar month holds everything Analytics and the
 * Quality grade need, so a portfolio view reads ~80 rows a year instead of
 * every audit with its JSON:
 *  - `a`: each submitted audit of the month, compact (id, store, template,
 *    when, score, critical, party, conductor) — turned back into slim
 *    AuditRecords for the existing trend / stat-tile / auditor / grade code;
 *  - `t`: per template row id, the section-score and question pass/fail
 *    totals (what sectionAverages / questionFailureRates add up per audit) —
 *    DM/Auditor audits only (store self-audits never count in Analytics).
 * Built by the SERVER (on each audit credit, and by Rebuild) and by the
 * Phase 7 seeder — both from this one pure builder, so they can't disagree.
 * Rows are member-writable in principle (site admins, the seeder): the
 * parser is defensive, like parseFailureSummary. Server-safe (no browser or
 * Graph imports; `.js` paths) — the read-back helpers live in
 * districtSummaryRead.ts.
 */

export const SUMMARY_VERSION = 1;
/** A district-month can't list more audits than this (≈ daily walkthroughs × 15 stores × 31 days). */
export const MAX_SUMMARY_AUDITS = 600;
/** Section / question keys kept per template (junk-key guard; real templates have < 200). */
export const MAX_KEYS = 400;

export interface SummaryAudit {
  /** Audit item id. */
  i: number;
  /** Store item id. */
  s: number;
  /** Template row id (version-pinned, as on the audit). */
  t: number;
  /** Template version. */
  v: number;
  /** SubmittedAt (ISO). */
  at: string;
  /** Score 0–100, or null (score-less form / all N/A). */
  sc: number | null;
  /** Critical failure. */
  c?: 1;
  /** Party when not the DM ('auditor' | 'store'). */
  p?: 'auditor' | 'store';
  /** Conductor's sign-in address. */
  u: string;
}

/** Per template row id: section score [sum, count] and question [checked, failed, audits]. */
export interface TemplateTotals {
  /** Submitted audits of this template in the month. */
  n: number;
  /** Of those, audits without a FailureSummary (submitted before 6b / unreadable). */
  un: number;
  sec: Record<string, [number, number]>;
  q: Record<string, [number, number, number]>;
}

export interface DistrictSummary {
  v: 1;
  /** Eastern calendar month, 'YYYY-MM'. */
  month: string;
  a: SummaryAudit[];
  t: Record<string, TemplateTotals>;
  /**
   * 1 = the section/question totals were DROPPED (the row would have passed
   * the column limit — server rowFields): `a` is complete, `t` is empty, and
   * readers count this month's audits as "not summarized" (QA 2026-10-22).
   */
  tx?: 1;
}

// --- Months (Eastern, the company's day) ----------------------------------------

const EASTERN = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit' });

/** 'YYYY-MM' of an instant in Eastern time ('' if unreadable). */
export function easternMonthOf(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const parts = EASTERN.formatToParts(new Date(ms));
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  return y && m ? `${y}-${m}` : '';
}

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isMonth(m: string): boolean {
  return MONTH.test(m);
}

/** 'YYYY-MM' + n months (n may be negative). */
export function addMonths(month: string, n: number): string {
  const m = MONTH.exec(month);
  if (!m) return '';
  const total = Number(m[1]) * 12 + (Number(m[2]) - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** The MonthStart column value for a month (a sort/filter key — midnight UTC on the 1st, not an instant). */
export function monthKey(month: string): string {
  return `${month}-01T00:00:00Z`;
}

/**
 * The SubmittedAt window that surely covers an Eastern month: one day wider
 * on each side (UTC vs Eastern), filtered exactly by easternMonthOf after.
 */
export function monthQueryWindow(month: string): { from: string; to: string } {
  const start = Date.parse(monthKey(month));
  const end = Date.parse(monthKey(addMonths(month, 1)));
  return { from: new Date(start - 86_400_000).toISOString(), to: new Date(end + 86_400_000).toISOString() };
}

// --- Build ---------------------------------------------------------------------------

const validScore = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= 100;

/**
 * Section / question ids come from member-written audit JSON (security review
 * 2026-10-22): real ids are ≤ 22 characters, so anything longer is junk —
 * dropped, so padding one audit can't blow up the month's row.
 */
export const MAX_KEY_LENGTH = 64;
const usableKey = (id: string) => id.length > 0 && id.length <= MAX_KEY_LENGTH;
/** Own data property — a "__proto__" / "constructor" key is just a key. */
function put<T>(o: Record<string, T>, k: string, v: T): void {
  Object.defineProperty(o, k, { value: v, enumerable: true, writable: true, configurable: true });
}

/** What the builder reads of an audit (an AuditRecord fits; the server and seeder map list fields with summaryInputFromFields). */
export interface SummaryInput {
  id: number;
  storeId: number;
  templateId: number;
  templateVersion: number;
  auditorUpn: string;
  status: string;
  submittedAt: string;
  score: number | null;
  criticalFailed: boolean;
  sectionScores: Record<string, number | null> | null;
  failureSummary: FailureSummary | null;
  party: Party;
}

/** Audits list fields (Graph `fields` / SharePoint REST) → SummaryInput, as toAuditRecord reads them. */
export function summaryInputFromFields(id: number | string, f: Record<string, unknown>): SummaryInput {
  let sectionScores: Record<string, number | null> | null = null;
  if (typeof f.SectionScores === 'string' && f.SectionScores) {
    try {
      const v: unknown = JSON.parse(f.SectionScores);
      if (v && typeof v === 'object' && !Array.isArray(v)) sectionScores = v as Record<string, number | null>;
    } catch {
      // cosmetic — dropped
    }
  }
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x)) ? Number(x) : 0);
  return {
    id: Number(id),
    storeId: num(f.StoreId),
    templateId: num(f.TemplateId),
    templateVersion: num(f.TemplateVersion),
    auditorUpn: typeof f.AuditorUpn === 'string' ? f.AuditorUpn : '',
    status: typeof f.AuditStatus === 'string' ? f.AuditStatus : '',
    submittedAt: typeof f.SubmittedAt === 'string' ? f.SubmittedAt : '',
    score: validScore(f.Score) ? f.Score : null,
    criticalFailed: f.CriticalFailed === true,
    sectionScores,
    failureSummary: parseFailureSummary(typeof f.FailureSummary === 'string' ? f.FailureSummary : null),
    party: partyOf(f.Party),
  };
}

/**
 * The summary of one district-month from its submitted audits (the caller
 * picks the district's stores and the Eastern month; anything else here is
 * dropped). Deterministic: audits sorted by id, keys in insertion order of
 * that sort.
 */
export function buildDistrictSummary(month: string, audits: readonly SummaryInput[]): DistrictSummary {
  // Past the cap, the NEWEST audits are kept — the ones grades read (QA 2026-10-22).
  const mine = audits
    .filter((a) => a.status === 'submitted' && easternMonthOf(a.submittedAt) === month)
    .sort((x, y) => y.submittedAt.localeCompare(x.submittedAt) || y.id - x.id)
    .slice(0, MAX_SUMMARY_AUDITS)
    .sort((x, y) => x.id - y.id);
  const t: Record<string, TemplateTotals> = {};
  const a: SummaryAudit[] = [];
  for (const r of mine) {
    const party = partyOf(r.party);
    a.push({
      i: r.id,
      s: r.storeId,
      t: r.templateId,
      v: r.templateVersion,
      at: r.submittedAt,
      sc: validScore(r.score) ? r.score : null,
      ...(r.criticalFailed ? { c: 1 as const } : {}),
      ...(party === 'dm' ? {} : { p: party }),
      u: r.auditorUpn.trim(),
    });
    // Store teams' self-audits stay out of the totals, as Analytics leaves them
    // out (security review 2026-10-19); they're still listed in `a` (Home lists).
    if (party === 'store') continue;
    const key = String(r.templateId);
    const tot = (t[key] ??= { n: 0, un: 0, sec: {}, q: {} });
    tot.n += 1;
    for (const [id, score] of Object.entries(r.sectionScores ?? {})) {
      if (!validScore(score) || !usableKey(id)) continue;
      const has = Object.hasOwn(tot.sec, id);
      if (!has && Object.keys(tot.sec).length >= MAX_KEYS) continue;
      const cur: [number, number] = has ? tot.sec[id]! : [0, 0];
      put(tot.sec, id, [cur[0] + score, cur[1] + 1]);
    }
    const sum = r.failureSummary;
    if (!sum) {
      tot.un += 1;
      continue;
    }
    const add = (id: string, passed: number, failed: number) => {
      if (!usableKey(id)) return;
      const has = Object.hasOwn(tot.q, id);
      if (!has && Object.keys(tot.q).length >= MAX_KEYS) return;
      const cur: [number, number, number] = has ? tot.q[id]! : [0, 0, 0];
      put(tot.q, id, [cur[0] + passed + failed, cur[1] + failed, cur[2] + 1]);
    };
    for (const [id, res] of Object.entries(sum.q)) add(id, res === 'p' ? 1 : 0, res === 'f' ? 1 : 0);
    for (const [id, [p, f]] of Object.entries(sum.g)) add(id, p, f);
  }
  return { v: SUMMARY_VERSION, month, a, t };
}

// --- Parse (defensive) ------------------------------------------------------------

const whole = (x: unknown): x is number => typeof x === 'number' && Number.isInteger(x) && x >= 0;
const count = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0;

/** A stored summary, or null when unreadable. Bad entries are dropped, never trusted. */
export function parseDistrictSummary(raw: unknown): DistrictSummary | null {
  let v: unknown = raw;
  if (typeof raw === 'string') {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (o.v !== SUMMARY_VERSION || typeof o.month !== 'string' || !isMonth(o.month) || !Array.isArray(o.a)) return null;
  const a: SummaryAudit[] = [];
  // Stored sorted by id: past the cap keep the end (the newest).
  for (const e of o.a.slice(-MAX_SUMMARY_AUDITS)) {
    if (!e || typeof e !== 'object') continue;
    const x = e as Record<string, unknown>;
    if (!whole(x.i) || x.i === 0 || !whole(x.s) || !whole(x.t) || typeof x.at !== 'string' || !Number.isFinite(Date.parse(x.at))) continue;
    a.push({
      i: x.i,
      s: x.s,
      t: x.t,
      v: whole(x.v) ? x.v : 0,
      at: x.at,
      sc: validScore(x.sc) ? x.sc : null,
      ...(x.c === 1 ? { c: 1 as const } : {}),
      ...(x.p === 'auditor' || x.p === 'store' ? { p: x.p } : {}),
      u: typeof x.u === 'string' ? x.u.slice(0, 320) : '',
    });
  }
  const t: Record<string, TemplateTotals> = {};
  if (o.t && typeof o.t === 'object' && !Array.isArray(o.t)) {
    for (const [key, val] of Object.entries(o.t as Record<string, unknown>)) {
      if (!/^\d{1,10}$/.test(key) || !val || typeof val !== 'object') continue;
      const tv = val as Record<string, unknown>;
      const sec: Record<string, [number, number]> = {};
      const q: Record<string, [number, number, number]> = {};
      if (tv.sec && typeof tv.sec === 'object') {
        for (const [id, pair] of Object.entries(tv.sec as Record<string, unknown>).slice(0, MAX_KEYS)) {
          if (!usableKey(id)) continue;
          if (!Array.isArray(pair) || !count(pair[0]) || !whole(pair[1]) || pair[1] === 0 || pair[0] > 100 * pair[1]) continue;
          Object.defineProperty(sec, id, { value: [pair[0], pair[1]], enumerable: true, writable: true, configurable: true });
        }
      }
      if (tv.q && typeof tv.q === 'object') {
        for (const [id, tri] of Object.entries(tv.q as Record<string, unknown>).slice(0, MAX_KEYS)) {
          if (!usableKey(id)) continue;
          if (!Array.isArray(tri) || !whole(tri[0]) || !whole(tri[1]) || !whole(tri[2]) || tri[1] > tri[0]) continue;
          Object.defineProperty(q, id, { value: [tri[0], tri[1], tri[2]], enumerable: true, writable: true, configurable: true });
        }
      }
      Object.defineProperty(t, key, {
        value: { n: whole(tv.n) ? tv.n : 0, un: whole(tv.un) ? tv.un : 0, sec, q },
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
  }
  return { v: SUMMARY_VERSION, month: o.month, a, t, ...(o.tx === 1 ? { tx: 1 as const } : {}) };
}
